import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { ArrowRightLeft, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { fetchFamilies, fetchSeasons } from "@/lib/api/manufacturing";
import {
  buildTransferPreview,
  executeTransfer,
  fetchSeasonDatePresets,
  fetchTags,
  fetchTransferCandidates,
} from "@/lib/api/productTransfer";
import { TransferPreviewDialog } from "@/components/utilities/TransferPreviewDialog";

const statusOptions = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Activos" },
  { value: "obsolete", label: "Obsoletos" },
];

export function ProductTransferUtility() {
  const { toast } = useToast();

  const [candidates, setCandidates] = React.useState([]);
  const [families, setFamilies] = React.useState([]);
  const [fabricationSeasons, setFabricationSeasons] = React.useState([]);
  const [seasonPresets, setSeasonPresets] = React.useState([]);
  const [tags, setTags] = React.useState([]);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [fabricationSeasonFilter, setFabricationSeasonFilter] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");

  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [seasonId, setSeasonId] = React.useState("");
  const [tagId, setTagId] = React.useState("");

  const [loading, setLoading] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [executing, setExecuting] = React.useState(false);

  const selectedSeason = seasonPresets.find((preset) => preset.id === seasonId) || null;
  const selectedTag = tags.find((tag) => String(tag.id) === String(tagId)) || null;

  const loadCandidates = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchTransferCandidates({
      searchTerm,
      status: statusFilter,
      temporada: fabricationSeasonFilter,
      codFam: familyFilter,
      limit: 500,
    });
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los articulos", variant: "destructive" });
      setCandidates([]);
    } else {
      setCandidates(result.data || []);
      setSelectedIds((prev) => {
        const available = new Set((result.data || []).map((p) => p.id));
        const next = new Set();
        prev.forEach((id) => {
          if (available.has(id)) next.add(id);
        });
        return next;
      });
    }
    setLoading(false);
  }, [searchTerm, statusFilter, fabricationSeasonFilter, familyFilter, toast]);

  React.useEffect(() => {
    (async () => {
      const [familiesResult, fabricationSeasonsResult, presets, tagsResult] = await Promise.all([
        fetchFamilies(),
        fetchSeasons(),
        fetchSeasonDatePresets(),
        fetchTags(),
      ]);
      setFamilies(familiesResult.data || []);
      setFabricationSeasons(fabricationSeasonsResult.data || []);
      setSeasonPresets(presets || []);
      setTags(tagsResult.data || []);
    })();
  }, []);

  React.useEffect(() => {
    const timeout = setTimeout(loadCandidates, 250);
    return () => clearTimeout(timeout);
  }, [loadCandidates]);

  const allSelected = candidates.length > 0 && selectedIds.size === candidates.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(() => {
      if (allSelected) return new Set();
      return new Set(candidates.map((p) => p.id));
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePreview = () => {
    if (selectedIds.size === 0) {
      toast({ title: "Selecciona productos", description: "Marca al menos un articulo para traspasar", variant: "destructive" });
      return;
    }
    if (!selectedSeason) {
      toast({ title: "Selecciona temporada", description: "Elige la temporada destino antes de continuar", variant: "destructive" });
      return;
    }
    const selectedProducts = candidates.filter((p) => selectedIds.has(p.id));
    const builtPreview = buildTransferPreview(selectedProducts, {
      seasonName: selectedSeason.name,
      tagName: selectedTag?.name || "",
    });
    setPreview(builtPreview);
    setPreviewOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedSeason) return;
    setExecuting(true);
    const result = await executeTransfer({
      ids: Array.from(selectedIds),
      seasonName: selectedSeason.name,
      tagId: selectedTag?.id || null,
    });
    setExecuting(false);

    if (result.error) {
      toast({
        title: "Error en el traspaso",
        description: result.error.message || "No se pudo completar el traspaso",
        variant: "destructive",
      });
      return;
    }

    const rows = result.data || [];
    const created = rows.filter((r) => r.action === "created").length;
    const updated = rows.filter((r) => r.action === "updated").length;
    toast({
      title: "Traspaso completado",
      description: `${created} creados, ${updated} actualizados (${rows.length} en total)`,
    });
    setPreviewOpen(false);
    setPreview(null);
    setSelectedIds(new Set());
    await loadCandidates();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <ArrowRightLeft className="h-5 w-5" />
            Traspaso de articulos a ventas
          </CardTitle>
          <CardDescription>
            Traspasa productos de fabricacion al catalogo comercial. Elige temporada y tag destino, selecciona los
            articulos (uno a uno o por filtro) y revisa el informe previo antes de ejecutar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Temporada destino (obligatoria)</label>
              <SeasonSelect data-season-selector="true"
                value={seasonId}
                onChange={(event) => setSeasonId(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Selecciona una temporada...</option>
                {seasonPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                    {preset.active === false ? " (inactiva)" : ""}
                  </option>
                ))}
              </SeasonSelect>
              {seasonPresets.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No hay temporadas configuradas en Ajustes (season_date_ranges).
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tag destino</label>
              <select
                value={tagId}
                onChange={(event) => setTagId(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Sin modificar tags</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                El tag elegido reemplaza los tags previos del producto en ventas.
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por codigo o descripcion..."
                className="pl-9"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <SeasonSelect data-season-selector="true"
              value={fabricationSeasonFilter}
              onChange={(event) => setFabricationSeasonFilter(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Todas las temporadas FAB</option>
              {fabricationSeasons.map((season) => (
                <option key={season.cod_tem} value={season.cod_tem.trim()}>
                  {season.cod_tem.trim()} - {season.descrip}
                </option>
              ))}
            </SeasonSelect>
            <select
              value={familyFilter}
              onChange={(event) => setFamilyFilter(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Todas las familias</option>
              {families.map((family) => (
                <option key={family.cod_fam} value={family.cod_fam}>
                  {family.cod_fam} - {family.descrip}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Badge variant="secondary">{selectedIds.size} seleccionados</Badge>
              <span>de {candidates.length} articulos</span>
              <Button type="button" variant="ghost" size="sm" onClick={loadCandidates} disabled={loading}>
                <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refrescar
              </Button>
            </div>
            <Button type="button" onClick={handlePreview} disabled={loading || selectedIds.size === 0}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              Generar informe previo
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 text-left text-xs uppercase text-gray-600">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleSelectAll}
                      aria-label="Seleccionar todo"
                    />
                  </th>
                  <th className="px-3 py-2">Codigo</th>
                  <th className="px-3 py-2">Descripcion</th>
                  <th className="px-3 py-2">Familia</th>
                  <th className="px-3 py-2">Temporada (fab.)</th>
                  <th className="px-3 py-2 text-right">Precio</th>
                  <th className="px-3 py-2">Estado ventas</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                      No hay articulos con los filtros actuales.
                    </td>
                  </tr>
                )}
                {candidates.map((product) => {
                  const checked = selectedIds.has(product.id);
                  return (
                    <tr
                      key={product.id}
                      className={`border-t hover:bg-gray-50 ${checked ? "bg-primary/5" : ""}`}
                      onClick={() => toggleSelect(product.id)}
                    >
                      <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(product.id)}
                          aria-label={`Seleccionar ${product.code}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono">{product.code}</td>
                      <td className="px-3 py-2">{product.description}</td>
                      <td className="px-3 py-2">{product.family?.descrip || product.cod_fam || "-"}</td>
                      <td className="px-3 py-2">{product.season?.descrip || product.temporada || "-"}</td>
                      <td className="px-3 py-2 text-right">{Number(product.precio_v || 0).toFixed(2)} €</td>
                      <td className="px-3 py-2">
                        {product.existsInSales ? (
                          <Badge variant="secondary">Existe</Badge>
                        ) : (
                          <Badge>Nuevo</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <TransferPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        preview={preview}
        executing={executing}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
